import { Feature, Overlay } from 'ol';
import Point from 'ol/geom/Point';
import olMap from 'ol/Map';
import Geolocation from 'ol/Geolocation';
import * as olProj from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Circle from "ol/style/Circle";
import Stroke from "ol/style/Stroke";
import { AppStorage } from "./AppStorage";
import TrailwiseEventBus from './TrailwiseEventBus';
import { Modal } from 'bootstrap';
import LineString from 'ol/geom/LineString';
import GeometryLayout from 'ol/geom/GeometryLayout';
import OverlayPositioning from 'ol/OverlayPositioning';
import BaseLayer from 'ol/layer/Base';
import { AppRecordGeoLocation } from './AppRecordGeoLocation';
import { Coordinate } from 'ol/coordinate';


export class AppGeoLocation {

    private geolocation: Geolocation;
    private locateMeFeature: Feature;
    //private trackMeFeature: Feature;
    private locationLayer: VectorLayer;
    private trackingLocation: boolean = false;
    private hasTouch: boolean;
    private appStorage: AppStorage;
    private trackingModal: Modal;
    private geolocationModal: Modal;
    private previousM = 0;
    private deltaMean = 500; // the geolocation sampling period mean in ms
    private markerEl: HTMLImageElement;
    private marker: Overlay;
    private recordGeoLocation: AppRecordGeoLocation;
    private updateViewTriggered: boolean = false;
    private previousPosition: Coordinate = null;
    private orientateWhilstTracking: boolean = false; 
    private didCenterOnLocation: boolean = false;
    // LineString to store the different geolocation positions. This LineString
    // is time aware.
    // The Z dimension is actually used to store the rotation (heading).
    private positions = new LineString([], GeometryLayout.XYZM);

    public constructor(private map: olMap, private baseLayer: BaseLayer) {
        this.init();
    }

    /** Ends tracking if it is running (used when another map mode takes over). */
    public stopTracking = (): void => {
        if (this.trackingLocation) {
            this.onTrackLocation();
        }
    }

    /** Shows/hides the locate-me control and its options flyout (hidden while editing). */
    public setButtonsVisible = (visible: boolean): void => {
        const controls = $('#btnGoToCurrentLocation').parent('.ol-geolocation');
        if (visible) {
            controls.removeClass('d-none');
        } else {
            controls.addClass('d-none');
            $('#geolocationOptions').hide();
        }
    }

    private init = (): void => {
        let self = this

        this.geolocationModal = new Modal(document.getElementById('geoLocationModal'));
        this.trackingModal = new Modal(document.getElementById('trackingModal'));
        this.hasTouch = ('ontouchstart' in window || navigator.msMaxTouchPoints > 0);
        this.addLocationLayer(this.map);
        this.appStorage = new AppStorage();
        this.recordGeoLocation = new AppRecordGeoLocation();
        this.geolocation = new Geolocation({
            tracking: false,
            projection: this.map.getView().getProjection(),
            trackingOptions: {
                enableHighAccuracy: true,
                maximumAge: 20000,
                timeout: 10000
            }
        });

        // Geolocation marker
        this.markerEl = document.getElementById('geolocation_marker') as HTMLImageElement;
        this.markerEl.style.display = "block";
        this.marker = new Overlay({
            positioning: OverlayPositioning.CENTER_CENTER,
            element: this.markerEl,
            stopEvent: false
        });



        // Listen to position changes
        this.geolocation.on('change', function () {
            const position = self.geolocation.getPosition();
            const accuracy = self.geolocation.getAccuracy();
            const heading = self.geolocation.getHeading() || 0;
            const speed = self.geolocation.getSpeed() || 0;
            const m = Date.now();
            let distance: number = 0;

            if (self.orientateWhilstTracking) {
                //if the distance is < 1.5m and speed < 0.7 do not update the location (causing map to spin when stationary)
                if (self.previousPosition !== null) {
                    let ls = new LineString([self.previousPosition, position])
                    distance = ls.getLength();
                    //console.log("Length:" + distance);
                    //console.log("Speed:" + speed);
                    if (distance < 2.5 && speed < 2) {
                        return;
                    }
                }

                TrailwiseEventBus.trackingGeolocationChange(self.geolocation);
                self.addPosition(position, heading, m, speed);

                const coords = self.positions.getCoordinates();
                const len = coords.length;
                if (len >= 2) {
                    self.deltaMean = (coords[len - 1][3] - coords[0][3]) / (len - 1);
                }
            }
            else {
                //no orientation
                if (self.didCenterOnLocation === false) {
                    //zoom in for start of tracking and from then on just centre the map
                    self.map.getView().animate({ center: position, zoom: 16 });
                    self.didCenterOnLocation = true;
                } else {
                    self.map.getView().setCenter(position);
                    self.marker.setPosition(position);
                }              
            }

            //const html = [
            //    'Position: ' + position[0].toFixed(2) + ', ' + position[1].toFixed(2),
            //    'Accuracy: ' + accuracy,
            //    'Heading: ' + Math.round(self.radToDeg(heading)) + '&deg;',
            //    'Speed: ' + (speed * 3.6).toFixed(1) + ' km/h',
            //    'Delta: ' + Math.round(self.deltaMean) + 'ms',
            //].join('<br />');
            //console.log('Position: ' + position[0].toFixed(2) + ', ' + position[1].toFixed(2));
            //console.log('Heading: ' + Math.round(self.radToDeg(heading)) + '&deg;');
            //document.getElementById('info').innerHTML = html;
        });

        this.geolocation.on('error', function () {                       
            document.getElementById('btnStartTracking').removeEventListener('click', self.onStartTracking);
            document.getElementById('trackingModal').removeEventListener('hidden.bs.modal', self.onStartTracking);

            $('.ol-zoom').show();
            $('#btnTrackMeEnd').hide();
            $('#btnTrackMe').show();
            $('#btnLocateMe').removeAttr('disabled');

            if (self.trackingLocation) {
                //turn tracking off
                self.onTrackLocation();
            }

            self.geolocationModal.show();
            // FIXME we should remove the coordinates in positions
        });

        this.trackingLocation = false;

        //add touch class so that the button re-sizes the same as zoom controls
        if (this.hasTouch) {
            $('#btnGoToCurrentLocation').parent('.ol-geolocation').addClass('ol-touch');
        }

        $('#btnGoToCurrentLocation').on("click", function (event) {
            self.toggleOptionsDialogue(10);
        });

        let onModalClose = function () {
            if (self.trackingLocation === false) {
                self.toggleOptionsDialogue(1000);
            }   
        }

        //track me
        $('#btnTrackMe').on('click', function () {
            //self.showTrackingModal();
            self.updateViewTriggered = false;
            self.trackingModal.show();           

            document.getElementById('btnStartTracking').addEventListener('click', self.onStartTracking);            
            document.getElementById('trackingModal').addEventListener('hidden.bs.modal', onModalClose);
        });

        $('#btnTrackMeEnd').on('click', function () {
            document.getElementById('btnStartTracking').removeEventListener('click', self.onStartTracking);            
            document.getElementById('trackingModal').removeEventListener('hidden.bs.modal', onModalClose);

            $('#btnTrackMeEnd').hide();
            $('#btnTrackMe').show();
            $('#btnLocateMe').removeAttr('disabled');
            self.onTrackLocation();
            self.toggleOptionsDialogue(1000);
        });

        //locate me
        $('#btnLocateMe').on('click', function () {
            self.onLocateMe();
        });

        //got this
        //$('#ckTrackingGotThis').on('change', function () {
        //    self.appStorage.setValue("TrackingGotThis", $(this).prop("checked"));
        //});

        //this.initSimulate();
    }

    private onStartTracking = (): void => {        
        const self = this;
        $('#btnTrackMe').hide();
        $('#btnTrackMeEnd').show();
        $('#btnLocateMe').attr('disabled', 'disabled');        
                
        self.orientateWhilstTracking = $("#rdoTrackingOrientate").prop("checked");
        self.toggleOptionsDialogue(2500);
        self.onTrackLocation();                        
        self.trackingModal.hide();

        window.setTimeout(function () {
            if (!self.updateViewTriggered) {
                //nudge the map, if starting tracking from the last tracking point (ie the map hasn't moved) then postrender won't be firing until there is some other interaction with the map
                self.map.getView().adjustCenter([1,1]);                
            }
        }, 500);
    }

    private addPosition = (position, heading, m, speed): void => {
        const x = position[0];
        const y = position[1];
        const fCoords = this.positions.getCoordinates();
        const previous = fCoords[fCoords.length - 1];
        const prevHeading = previous && previous[2];
        if (prevHeading) {
            let headingDiff = heading - this.mod(prevHeading);

            // force the rotation change to be less than 180°
            if (Math.abs(headingDiff) > Math.PI) {
                const sign = headingDiff >= 0 ? 1 : -1;
                headingDiff = -sign * (2 * Math.PI - Math.abs(headingDiff));
            }
            heading = prevHeading + headingDiff;
        }
        this.positions.appendCoordinate([x, y, heading, m]);

        // only keep the 20 last coordinates
        this.positions.setCoordinates(this.positions.getCoordinates().slice(-20));

        // FIXME use speed instead
        if (heading && speed) {
            this.markerEl.src = '/images/geolocation_marker_heading2.png';
        } else {
            this.markerEl.src = '/images/geolocation_marker_2.png';
        }

        this.previousPosition = position;
    }

    private updateView = (): void => {
        const self = this;

        // use sampling period to get a smooth transition
        let m = Date.now() - this.deltaMean * 1.5;
        m = Math.max(m, self.previousM);
        self.previousM = m;
        // interpolate position along positions LineString
        const c = self.positions.getCoordinateAtM(m, true);
        if (c) {
            self.map.getView().setCenter(self.getCenterWithHeading(c, -c[2], self.map.getView().getResolution()));
            self.map.getView().setRotation(-c[2]);
            self.marker.setPosition(c);
            self.map.render();
            self.updateViewTriggered = true;
        }
    }

    // recenters the view by putting the given coordinates at 3/4 from the top of
    // the screen
    private getCenterWithHeading = (position, rotation, resolution): [number, any] => {
        const size = this.map.getSize();
        const height = size[1];

        return [
            position[0] - (Math.sin(rotation) * height * resolution * 1) / 4,
            position[1] + (Math.cos(rotation) * height * resolution * 1) / 4,
        ];
    }

    private showTrackingModal = (): void => {
        //check if the user has dismissed this before        
        if (this.appStorage.getValue("TrackingGotThis") !== "true") {
            this.trackingModal.show();
        }
    }

    private onTrackLocation = (): void => {        
        let self = this;

        if (self.trackingLocation === false) {
            TrailwiseEventBus.trackingStart("");

            self.previousPosition = null;
            self.map.getView().setZoom(16);            
            self.map.addOverlay(self.marker);

            self.geolocation.setTracking(true);

            if (self.orientateWhilstTracking) {
                //hide zoom
                $('.ol-zoom').hide();
                self.markerEl.src = '/images/geolocation_marker_heading2.png';                                
                self.baseLayer.on('postrender', self.updateView);
                self.map.render();               
            }
            else {   
                self.map.getView().setRotation(0);
                self.didCenterOnLocation = false;
                self.markerEl.src = '/images/geolocation_marker_2.png';
            }     

            if (self.locationLayer.getSource().getFeatures().length > 0) {
                self.locationLayer.getSource().removeFeature(self.locateMeFeature);
            }
            self.trackingLocation = true;
            $('#btnGoToCurrentLocation').parent('.ol-geolocation').addClass('ol-control-active');
        }
        else {
            //show zoom
            $('.ol-zoom').show();

            //already tracking but button clicked again
            //remove handler
            self.baseLayer.un('postrender', self.updateView);

            //raise event
            TrailwiseEventBus.trackingEnd("");

            self.geolocation.setTracking(false);
            self.map.removeOverlay(self.marker);
            self.map.getView().setRotation(0);
            self.trackingLocation = false;
            $('#btnGoToCurrentLocation').parent('.ol-geolocation').removeClass('ol-control-active');
        }
    }

    private toggleOptionsDialogue = (delayInMilliseconds: number): void => {
        window.setTimeout(function () {
            $('#geolocationOptions').animate({ width: 'toggle', opacity: 'toggle' });
        }, delayInMilliseconds);
    }

    private onLocateMe = (): void => {
        let self = this;
        let callBack = function (coords, postitionFeature, error) {

            if (coords && !error) {
                self.map.getView().animate({ center: coords, zoom: 16 });

                self.toggleOptionsDialogue(1500);

                if (self.locationLayer.getSource().getFeatures().length > 0) {
                    //always try to remove feature first
                    self.locationLayer.getSource().removeFeature(postitionFeature);
                }

                //addfeature and remove after 15 seconds
                self.locationLayer.getSource().addFeature(postitionFeature);

                window.setTimeout(function () {
                    if (self.locationLayer.getSource().getFeatures().length > 0 && !self.trackingLocation) {
                        self.locationLayer.getSource().removeFeature(postitionFeature);
                    }
                }, 15000);
            };
        }

        this.getCurrentPosition(callBack);
    }

    private getCurrentPosition = (callback): void => {
        let self = this;

        let onSuccess = function (position) {
            let coords = olProj.fromLonLat([position.coords.longitude, position.coords.latitude], "EPSG:3857");
            //console.log("coords:" + coords);

            self.locateMeFeature.setGeometry(coords ? new Point(coords) : null);
            callback(coords, self.locateMeFeature);
        };

        let onError = function (error) {
            self.geolocationModal.show();
            self.toggleOptionsDialogue(500);
            console.log("AppGeoLocation error: code=" + error.code + ", message=" + error.message);
            callback(null, null, error);
        };

        navigator.geolocation.getCurrentPosition(onSuccess, onError, this.geolocation.getTrackingOptions());
    }

    private addLocationLayer = (map: olMap): void => {
        this.locateMeFeature = new Feature({
            id: 'positionfeature',
            name: 'positionFeature'
        });

        this.locateMeFeature.setStyle(new Style({
            image: new Circle({
                radius: 10,
                fill: new Fill({
                    color: '#d98216'
                }),
                stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 45,
                })
            })
        }));

        this.locationLayer = new VectorLayer({
            //id: 'locationLayer',
            //name: 'locationLayer'
        });
        this.locationLayer.set("id", "locationLayer", true);
        this.locationLayer.set("name", "locationLayer", true);

        this.locationLayer.setSource(
            new VectorSource({
                features: [this.locateMeFeature]
            })
        );

        this.locationLayer.setVisible(true);
        map.addLayer(this.locationLayer);
    }

    private initSimulate = (): void => {
        let self = this;

        const simulatePositionChange = (position): void => {
            const coords = position.coords;
            self.geolocation.set('accuracy', coords.accuracy);
            self.geolocation.set('heading', self.degToRad(coords.heading));

            //already in 3857 for home demo
            //const projectedPosition = olProj.fromLonLat([coords.longitude, coords.latitude]);
            //self.geolocation.set('position', projectedPosition);

            self.geolocation.set('position', [coords.longitude, coords.latitude]);
            self.geolocation.set('speed', coords.speed);
            self.geolocation.changed();
        }

        // simulate device move
        let simulationData;
        const client = new XMLHttpRequest();
        client.open('GET', '/Data/geolocation-orientation.json');

        /**
         * Handle LocationData loading.
         */
        client.onload = function () {
            simulationData = JSON.parse(client.responseText).data;
        };
        client.send();

        const simulateBtn = document.getElementById('simulate');
        simulateBtn.addEventListener(
            'click',
            function () {                
                self.orientateWhilstTracking = true;
                self.map.getView().setZoom(16);
                TrailwiseEventBus.trackingStart("");
                self.map.addOverlay(self.marker);

                const coordinates = [...simulationData];

                const first = coordinates.shift();
                simulatePositionChange(first);

                function geolocate() {                    
                    const position = coordinates.shift();
                    if (!position) {
                        self.baseLayer.un('postrender', self.updateView);
                        TrailwiseEventBus.trackingEnd("");
                        self.map.removeOverlay(self.marker);
                        self.geolocation.setTracking(false);
                        self.trackingLocation = false;
                        return;
                    }

                    simulatePositionChange(position);
                    window.setTimeout(function () {
                        geolocate();
                    }, 1000);
                }
                geolocate();

                self.baseLayer.on('postrender', self.updateView);
                self.map.render();
            },
            false
        );
    }


    // convert radians to degrees
    private radToDeg = (rad: number): number => {
        return (rad * 360) / (Math.PI * 2);
    }

    // convert degrees to radians
    private degToRad = (deg: number): number => {
        return (deg * Math.PI * 2) / 360;
    }

    // modulo for negative values
    private mod = (n: number): number => {
        return ((n % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    }
}